import React, {useRef, useState, useEffect} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import NavBar from '../pages/NavBar/NavBar';
import Button from '../common/Button';
import Dropdown from '../../utils/components/Dropdown';
import InputText from '../common/InputText';
import { getIdMeBaseApiUrl } from '../../utils/UrlConstants';
import { toast } from 'react-toastify';
import { useMsal } from '@azure/msal-react';
import ReactQuill from 'react-quill';
import FooterData from "../pages/NavBar/FooterData";
import { checkUserAuthentication, handleUnauthenticatedUser } from "../../utils/authUtils";
import { base64Utils } from "../../utils/base64Utils";
import { useIdentityAppService } from '../../services/IdentityServices';

interface UserAppRequestDto {
    id: number;
    userId: number;
    userLogin: string;
    firstName: string;
    lastName: string;
    userEmail: string;
    applicationCode: string;
    applicationName: string;
    roleCode: string;
    roleName: string;
    assignedTo: string;
    status: string;
    operationalFlag: string;
    createdDate: string;
    modifiedDate: string;
    createdBy: string;
    modifiedBy: string;
    notes: string;
}

interface ApprovalRequest {
    requestId: number;
    action: string; // 'APPROVE' or 'REJECT'
    reviewedBy: string;
    reviewNotes?: string;
    assignOption?: string; // 'immediate' or 'date'
    startDate?: string;
    endDate?: string;
}

interface ApprovalResponse {
    success: boolean;
    message: string;
    requestId: number;
    newStatus: string;
    timestamp: string;
}

const RequestDetails: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        if (!checkUserAuthentication()) {
            handleUnauthenticatedUser();
            return;
        }
    }, []);
    const { accounts } = useMsal();

    // Get data from navigation state
    const request: UserAppRequestDto | null = location.state?.request || null;
    const assignedTo: string = location.state?.assignedTo || '';
    const returnData = location.state?.returnData || null;

    // Form state
    const [selectedAction, setSelectedAction] = useState<string>('');
    const [reviewNotes, setReviewNotes] = useState<string>('');
    const [submitting, setSubmitting] = useState(false);
    const [formErrors, setFormErrors] = useState<{ action?: string; reviewNotes?: string; general?: string; startDate?: string; endDate?: string }>({});
    const [fetchError, setFetchError] = useState<string>('');

    // Assignment option state
    const [assignOption, setAssignOption] = useState<'immediate' | 'date'>('date');

    // Date state - Initialize with current date and calculated end date
    const [startDate, setStartDate] = useState<string>(() => {
        const today = new Date();
        return today.toISOString().split('T')[0]; // Format: YYYY-MM-DD
    });
    const [endDate, setEndDate] = useState<string>(() => {
        const today = new Date();
        const endDate = new Date(today);
        endDate.setDate(today.getDate() + 364); // Add 364 days
        return endDate.toISOString().split('T')[0]; // Format: YYYY-MM-DD
    });

    // Request lifecycle state
    const [requestLifecycle, setRequestLifecycle] = useState<UserAppRequestDto[]>([]);
    const [loadingLifecycle, setLoadingLifecycle] = useState(false);

    // Dropdown choices for approval actions
    const actionChoices = [
        { value: 'APPROVE', label: 'Approve' },
        { value: 'REJECT', label: 'Reject' }
    ];

    // Function to get status styles based on status value
    const getStatusStyles = (status: string) => {
        switch (status?.toUpperCase()) {
            case 'APPROVED':
                return {
                    backgroundColor: '#E8F5E8',
                    color: '#4CAF50'
                };
            case 'REJECTED':
                return {
                    backgroundColor: '#FFEBEE',
                    color: '#f44336'
                };
            case 'REQUESTED':
                return {
                    backgroundColor: '#fff3cd',
                    color: '#856404'
                };
            case 'SCHEDULED_ASSIGNMENT':
                return {
                    backgroundColor: '#E3F2FD',
                    color: '#1976D2'
                };
            case 'ERROR':
                return {
                    backgroundColor: '#FFF3E0',
                    color: '#FF9800'
                };
            default:
                return {
                    backgroundColor: '#f5f5f5',
                    color: '#666'
                };
        }
    };

    // Handle action dropdown change
    const handleActionChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const action = event.target.value;
        setSelectedAction(action);

        // Clear errors when user makes selection
        if (formErrors.action) {
            setFormErrors(prev => ({ ...prev, action: undefined }));
        }
    };

    // Handle review notes change
    const handleReviewNotesChange = (value: string) => {
        setReviewNotes(value);

        // Clear errors when user starts typing
        if (formErrors.reviewNotes) {
            setFormErrors(prev => ({ ...prev, reviewNotes: undefined }));
        }
    };

    // Handle assign option change
    const handleAssignOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const option = event.target.value as 'immediate' | 'date';
        setAssignOption(option);

        // If "Assign Immediately" is selected, set start date to today
        if (option === 'immediate') {
            const today = new Date();
            const todayString = today.toISOString().split('T')[0];
            setStartDate(todayString);

            // Recalculate end date based on today
            const newEndDate = calculateEndDate(todayString);
            setEndDate(newEndDate);
        }
    };

    // Calculate end date when start date changes
    const calculateEndDate = (startDateValue: string): string => {
        if (!startDateValue) return '';

        const start = new Date(startDateValue);
        // Check if date is valid
        if (isNaN(start.getTime())) return '';

        const end = new Date(start);
        end.setDate(start.getDate() + 364); // Add 364 days
        return end.toISOString().split('T')[0];
    };

    // Handle start date change
    const handleStartDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newStartDate = event.target.value;
        setStartDate(newStartDate);

        // Check if date is in the past (using local timezone)
        if (newStartDate) {
            // Get today's date in local timezone as YYYY-MM-DD
            const today = new Date();
            const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

            if (newStartDate < todayString) {
                setFormErrors(prev => ({ ...prev, startDate: 'Scheduled start date cannot be in Past.' }));
            } else {
                // Clear error if date is valid
                setFormErrors(prev => ({ ...prev, startDate: undefined }));

                // Calculate and set new end date only if start date is valid
                const newEndDate = calculateEndDate(newStartDate);
                if (newEndDate) {
                    setEndDate(newEndDate);
                }
            }

            // Re-validate end date if it exists
            if (endDate) {
                const start = new Date(newStartDate);
                const end = new Date(endDate);

                if (end <= start) {
                    setFormErrors(prev => ({ ...prev, endDate: 'End date should be after start date' }));
                } else {
                    setFormErrors(prev => ({ ...prev, endDate: undefined }));
                }
            }
        } else {
            // Clear errors if field is empty
            setFormErrors(prev => ({ ...prev, startDate: undefined }));
        }
    };

    // Handle end date change
    const handleEndDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newEndDate = event.target.value;
        setEndDate(newEndDate);

        // Validate end date against start date
        if (newEndDate && startDate) {
            const start = new Date(startDate);
            const end = new Date(newEndDate);

            if (end <= start) {
                setFormErrors(prev => ({ ...prev, endDate: 'End date should be after start date' }));
            } else {
                // Clear error if date is valid
                setFormErrors(prev => ({ ...prev, endDate: undefined }));
            }
        } else {
            // Clear error if field is empty
            setFormErrors(prev => ({ ...prev, endDate: undefined }));
        }
    };

    // Validate form
    const validateForm = (): boolean => {
        const errors: { action?: string; reviewNotes?: string; startDate?: string; endDate?: string } = {};

        if (!selectedAction) {
            errors.action = 'Please select an action (Approve or Reject)';
        }

        // Make review notes mandatory for all actions
        if (!reviewNotes || reviewNotes.trim().length === 0) {
            errors.reviewNotes = 'Review notes are required';
        }

        // Validate start date if action is approve
        if (selectedAction === 'Approve' && assignOption === 'date') {
            if (!startDate) {
                errors.startDate = 'Start date is required';
            } else {
                // Check if start date is in the past (using local timezone)
                const today = new Date();
                const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

                if (startDate < todayString) {
                    errors.startDate = 'Scheduled start date cannot be in Past.';
                }
            }

            if (!endDate) {
                errors.endDate = 'End date is required';
            } else if (startDate && endDate) {
                // Validate end date is after start date
                const start = new Date(startDate);
                const end = new Date(endDate);

                if (end <= start) {
                    errors.endDate = 'End date should be after start date';
                }
            }
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Handle form submission
    const handleSubmit = async () => {
        // Clear any previous errors
        setFormErrors({});

        if (!validateForm()) {
            const errorMsg = 'Please complete all required fields before submitting';
            setFormErrors(prev => ({ ...prev, general: errorMsg }));
            toast.error(errorMsg);
            return;
        }

        if (!request) {
            const errorMsg = 'No request data available';
            setFormErrors(prev => ({ ...prev, general: errorMsg }));
            toast.error(errorMsg);
            return;
        }

        setSubmitting(true);

        try {
            const approvalData: ApprovalRequest = {
                requestId: request.id,
                action: selectedAction,
                reviewedBy: accounts[0]?.idTokenClaims?.oid || accounts[0]?.name || accounts[0]?.username || assignedTo,
                reviewNotes: base64Utils.encode(reviewNotes.trim() || `Request ${selectedAction.toLowerCase()}d by ${accounts[0]?.name || assignedTo}`)
            };

            // Add assignment option and dates if action is APPROVE
            if (selectedAction === 'APPROVE') {
                approvalData.assignOption = assignOption;
                approvalData.startDate = startDate;
                approvalData.endDate = endDate;
            }

            console.log('Submitting approval decision:', approvalData);

            const response = await useIdentityAppService().postApproveRequest(JSON.stringify(approvalData));

            if (response.ok) {
                const data: ApprovalResponse = await response.json();
                console.log('Approval response:', data);

                // Determine success/failure based on success flag and newStatus field
                const isSuccess = data.success !== false && (data.newStatus === 'APPROVED' || data.newStatus === 'REJECTED' || data.newStatus === 'SCHEDULED_ASSIGNMENT');
                const isError = data.newStatus === 'ERROR' || data.success === false;

                if (isSuccess) {
                    // Show success message from API response
                    toast.success(data.message || `Request ${data.newStatus.toLowerCase()} successfully`, {
                        autoClose: 3000
                    });

                    console.log('Request processed successfully:', {
                        requestId: request.id,
                        action: selectedAction,
                        newStatus: data.newStatus
                    });

                    // Navigate back to previous page
                    setTimeout(() => {
                        if (returnData?.returnPath) {
                            // Navigate to the specified return path (e.g., from UserManagement)
                            navigate(returnData.returnPath, { state: returnData });
                        } else if (returnData) {
                            // If we have return data, go back to request history
                            navigate('/request-history', { state: returnData });
                        } else {
                            // Otherwise go to admin dashboard
                            navigate('/admin-dashboard');
                        }
                    }, 1500);
                } else if (isError) {
                    // Handle backend failure - Backend returned error status
                    const errorMessage = data.message || 'Request processing failed';

                    console.error('Request processing failed:', {
                        requestId: request.id,
                        action: selectedAction,
                        newStatus: data.newStatus,
                        message: data.message,
                        success: data.success
                    });

                    // Show error toast
                    toast.error(errorMessage, { autoClose: 7000 });

                    // Set error in state for display
                    setFormErrors(prev => ({ ...prev, general: errorMessage }));
                } else {
                    // Unexpected status
                    const errorMessage = data.message || `Unexpected response status: ${data.newStatus}`;
                    console.warn('Unexpected response:', data);

                    toast.warning(errorMessage, { autoClose: 6000 });
                    setFormErrors(prev => ({ ...prev, general: errorMessage }));
                }
            } else {
                // Handle HTTP errors (non-200 responses)
                let errorData: any;
                try {
                    errorData = await response.json();
                } catch (parseError) {
                    errorData = {
                        error: 'Failed to parse error response',
                        message: `HTTP ${response.status}: ${response.statusText}`
                    };
                }

                const httpError = errorData.error || errorData.message ||
                                `HTTP ${response.status}: ${response.statusText}`;

                console.error('HTTP error during approval:', {
                    status: response.status,
                    statusText: response.statusText,
                    errorData: errorData,
                    requestId: request.id,
                    action: selectedAction
                });

                toast.error(`Request failed: ${httpError}`, { autoClose: 7000 });
                setFormErrors(prev => ({ ...prev, general: `Request failed: ${httpError}` }));
            }

        } catch (error) {
            console.error('Error during approval request:', error);

            // Show specific error message
            const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
            const fullError = `Approval request failed: ${errorMessage}`;

            toast.error(fullError, { autoClose: 7000 });
            setFormErrors(prev => ({ ...prev, general: fullError }));

            // Log additional details for debugging
            console.error('Approval data that failed:', {
                requestId: request?.id,
                action: selectedAction,
                assignOption: assignOption,
                startDate: startDate,
                endDate: endDate,
                error: error
            });
        } finally {
            setSubmitting(false);
        }
    };

    // Handle cancel button
    const handleCancel = () => {
        if (returnData?.returnPath) {
            // Navigate to the specified return path (e.g., from UserManagement)
            navigate(returnData.returnPath, { state: returnData });
        } else if (returnData) {
            // Go back to request history if we have return data
            navigate('/request-history', { state: returnData });
        } else {
            // Otherwise go to admin dashboard
            navigate('/admin-dashboard');
        }
    };

    // Fetch request lifecycle
    const fetchRequestLifecycle = async () => {
        if (!request) return;

        setLoadingLifecycle(true);
        try {
            const response = await useIdentityAppService().fetchRequestLifecycle(request.id);
            
            if (response.ok) {
                const lifecycleData: UserAppRequestDto[] = await response.json();
                setRequestLifecycle(lifecycleData);
            } else {
                console.error('Failed to fetch request lifecycle:', response.statusText);
                setFetchError('Failed to load request history');
            }
        } catch (error) {
            console.error('Error fetching request lifecycle:', error);
            setFetchError('Error loading request history');
        } finally {
            setLoadingLifecycle(false);
        }
    };

    // Redirect if no request data
    React.useEffect(() => {
        if (!request) {
            setFetchError('No request data found. Redirecting to Request History.');
            navigate('/admin-dashboard');
        }
    }, [request, navigate]);

    // Fetch request lifecycle when component mounts
    useEffect(() => {
        if (request) {
            fetchRequestLifecycle();
        }
    }, [request]);

    if (!request) {
        return <div>Loading...</div>;
    }

    // Get status styles for current request
    const statusStyles = getStatusStyles(request.status);

    return (
        <React.Fragment>
            <NavBar />

            <div style={{ marginLeft: '20px', marginRight: '20px', minHeight: '300px' }}>
                <div className="subheading grid-row">
                    <div className="grid-col flex-1">
                        <h3 className="subheading-title" style={{
                            textAlign: 'center',
                            marginBottom: '20px',
                            marginTop: '40px'
                        }}>
                            Request Details
                        </h3>
                    </div>
                </div>

                {/* Fetch Error Display */}
                {fetchError && (
                    <div style={{
                        color: "red",
                        margin: "20px auto",
                        padding: "10px",
                        textAlign: "center",
                        maxWidth: "900px"
                    }}>
                        {fetchError}
                    </div>
                )}

                <div style={{
                    padding: '30px',
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    margin: '0 auto',
                    maxWidth: '900px',
                    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
                }}>
                    {/* Request Information Grid */}
                    <div style={{ lineHeight: '1.6', marginBottom: '40px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px', marginBottom: '30px' }}>
                            <div>
                                <h4 style={{ margin: '0 0 20px 0', color: '#00607f', borderBottom: '2px solid #00607f', paddingBottom: '8px' }}>
                                    User Information
                                </h4>
                                <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '6px' }}>
                                    <p style={{ margin: '8px 0' }}><strong>User ID:</strong> {request.userId}</p>
                                    <p style={{ margin: '8px 0' }}><strong>Username:</strong> {request.userLogin}</p>
                                    <p style={{ margin: '8px 0' }}><strong>Name:</strong> {request.firstName} {request.lastName}</p>
                                    <p style={{ margin: '8px 0' }}><strong>Email:</strong> {request.userEmail}</p>
                                </div>
                            </div>
                            <div>
                                <h4 style={{ margin: '0 0 20px 0', color: '#00607f', borderBottom: '2px solid #00607f', paddingBottom: '8px' }}>
                                    Request Information
                                </h4>
                                <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '6px' }}>
                                    <p style={{ margin: '8px 0' }}><strong>Request ID:</strong> {request.id}</p>
                                    <p style={{ margin: '8px 0' }}>
                                        <strong>Status:</strong>
                                        <span style={{
                                            padding: '4px 10px',
                                            borderRadius: '4px',
                                            backgroundColor: statusStyles.backgroundColor,
                                            color: statusStyles.color,
                                            marginLeft: '8px',
                                            fontSize: '14px',
                                            fontWeight: 'bold'
                                        }}>
                                            {request.status}
                                        </span>
                                    </p>
                                    <p style={{ margin: '8px 0' }}><strong>Assigned To:</strong> {request.assignedTo}</p>
                                    <p style={{ margin: '8px 0' }}><strong>Created:</strong> {new Date(request.createdDate).toLocaleString()}</p>
                                    <p style={{ margin: '8px 0' }}><strong>Modified:</strong> {new Date(request.modifiedDate).toLocaleString()}</p>
                                </div>
                            </div>
                        </div>

                        <div style={{ marginBottom: '30px' }}>
                            <h4 style={{ margin: '0 0 20px 0', color: '#00607f', borderBottom: '2px solid #00607f', paddingBottom: '8px' }}>
                                Role & Application Details
                            </h4>
                            <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '6px' }}>
                                <p style={{ margin: '8px 0' }}><strong>Application:</strong> {request.applicationName} ({request.applicationCode})</p>
                                <p style={{ margin: '8px 0' }}><strong>Role:</strong> {request.roleName} ({request.roleCode})</p>
                            </div>
                        </div>

                        {request.notes && (
                            <div style={{ marginBottom: '30px' }}>
                                <h4 style={{ margin: '0 0 20px 0', color: '#00607f', borderBottom: '2px solid #00607f', paddingBottom: '8px' }}>
                                    Notes
                                </h4>
                                <div style={{
                                    padding: '15px',
                                    backgroundColor: '#f8f9fa',
                                    borderRadius: '6px',
                                    border: '1px solid #dee2e6'
                                }}>
                                    {request.notes}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Request Lifecycle Timeline */}
                    <div style={{ marginBottom: '30px' }}>
                        <h4 style={{ margin: '0 0 20px 0', color: '#00607f', borderBottom: '2px solid #00607f', paddingBottom: '8px' }}>
                            Request History & Lifecycle
                        </h4>
                        {loadingLifecycle ? (
                            <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                                Loading request history...
                            </div>
                        ) : requestLifecycle.length > 0 ? (
                            <div style={{
                                backgroundColor: '#f8f9fa',
                                padding: '20px',
                                borderRadius: '6px',
                                border: '1px solid #dee2e6'
                            }}>
                                {requestLifecycle.map((lifecycleItem, index) => {
                                    const isFirst = index === 0;
                                    const isLast = index === requestLifecycle.length - 1;
                                    const itemStatusStyles = getStatusStyles(lifecycleItem.status);
                                    
                                    return (
                                        <div key={lifecycleItem.id} style={{ 
                                            position: 'relative',
                                            paddingLeft: '40px',
                                            paddingBottom: isLast ? '0' : '25px'
                                        }}>
                                            {/* Timeline line */}
                                            {!isLast && (
                                                <div style={{
                                                    position: 'absolute',
                                                    left: '15px',
                                                    top: '25px',
                                                    bottom: '0',
                                                    width: '2px',
                                                    backgroundColor: '#dee2e6'
                                                }} />
                                            )}
                                            
                                            {/* Timeline dot */}
                                            <div style={{
                                                position: 'absolute',
                                                left: '8px',
                                                top: '8px',
                                                width: '16px',
                                                height: '16px',
                                                borderRadius: '50%',
                                                backgroundColor: itemStatusStyles.color,
                                                border: '3px solid white',
                                                boxShadow: '0 0 0 2px ' + itemStatusStyles.color
                                            }} />
                                            
                                            {/* Timeline content */}
                                            <div style={{
                                                backgroundColor: 'white',
                                                padding: '15px',
                                                borderRadius: '6px',
                                                border: '1px solid #dee2e6',
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                            }}>
                                                <div style={{
                                                    display: 'flex',
                                                    justifyContent: 'space-between',
                                                    alignItems: 'flex-start',
                                                    marginBottom: '10px'
                                                }}>
                                                    <div>
                                                        <span style={{
                                                            padding: '3px 8px',
                                                            borderRadius: '4px',
                                                            backgroundColor: itemStatusStyles.backgroundColor,
                                                            color: itemStatusStyles.color,
                                                            fontSize: '12px',
                                                            fontWeight: 'bold',
                                                            marginRight: '10px'
                                                        }}>
                                                            {lifecycleItem.status}
                                                        </span>
                                                        <span style={{
                                                            fontSize: '12px',
                                                            color: '#666',
                                                            fontWeight: 'bold'
                                                        }}>
                                                            Request ID: {lifecycleItem.id}
                                                        </span>
                                                    </div>
                                                    <span style={{
                                                        fontSize: '12px',
                                                        color: '#666',
                                                        backgroundColor: lifecycleItem.operationalFlag === 'ACTIVE' ? '#e8f5e8' : '#f8f9fa',
                                                        padding: '2px 6px',
                                                        borderRadius: '3px',
                                                        border: '1px solid ' + (lifecycleItem.operationalFlag === 'ACTIVE' ? '#4CAF50' : '#dee2e6')
                                                    }}>
                                                        {lifecycleItem.operationalFlag}
                                                    </span>
                                                </div>
                                                
                                                <div style={{
                                                    display: 'grid',
                                                    gridTemplateColumns: '1fr 1fr',
                                                    gap: '15px',
                                                    fontSize: '14px'
                                                }}>
                                                    <div>
                                                        <p style={{ margin: '4px 0' }}>
                                                            <strong>Created:</strong> {new Date(lifecycleItem.createdDate).toLocaleString()}
                                                        </p>
                                                        <p style={{ margin: '4px 0' }}>
                                                            <strong>Created By:</strong> {lifecycleItem.createdBy || 'N/A'}
                                                        </p>
                                                        <p style={{ margin: '4px 0' }}>
                                                            <strong>Modified:</strong> {new Date(lifecycleItem.modifiedDate).toLocaleString()}
                                                        </p>
                                                        <p style={{ margin: '4px 0' }}>
                                                            <strong>Modified By:</strong> {lifecycleItem.modifiedBy || 'N/A'}
                                                        </p>
                                                    </div>
                                                    <div>
                                                        <p style={{ margin: '4px 0' }}>
                                                            <strong>Assigned To:</strong> {lifecycleItem.assignedTo}
                                                        </p>
                                                    </div>
                                                </div>
                                                
                                                {lifecycleItem.notes && (
                                                    <div style={{
                                                        marginTop: '10px',
                                                        padding: '8px',
                                                        backgroundColor: '#f8f9fa',
                                                        borderRadius: '4px',
                                                        fontSize: '13px'
                                                    }}>
                                                        <strong>Notes:</strong>
                                                        <div style={{ marginTop: '4px' }}>
                                                            {lifecycleItem.notes}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div style={{
                                textAlign: 'center',
                                padding: '20px',
                                color: '#666',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '6px',
                                border: '1px solid #dee2e6'
                            }}>
                                No request history available
                            </div>
                        )}
                    </div>

                    {/* Approval Section - Only show if status is REQUESTED or SCHEDULED_ASSIGNMENT */}
                    {(request.status?.toUpperCase() === 'REQUESTED' || request.status?.toUpperCase() === 'SCHEDULED_ASSIGNMENT') ? (
                        <div style={{
                            borderTop: '2px solid #dee2e6',
                            paddingTop: '30px',
                            marginTop: '30px'
                        }}>
                            <h4 style={{ margin: '0 0 25px 0', color: '#00607f', textAlign: 'center' }}>
                                Request Review
                            </h4>

                            {/* General Error Display - Moved to top of form */}
                            {formErrors.general && (
                                <div style={{
                                    color: "red",
                                    marginTop: "20px",
                                    marginBottom: "20px",
                                    padding: "10px",
                                    textAlign: "center"
                                }}>
                                    {formErrors.general}
                                </div>
                            )}

                            <div style={{
                                maxWidth: '600px',
                                margin: '0 auto'
                            }}>
                                {/* Action Dropdown */}
                                <div style={{ marginBottom: '25px', width: '100%' }}>
                                    <Dropdown
                                        choices={actionChoices}
                                        emptyChoiceLabel="Select Action"
                                        mandatoryLabel={true}
                                        name="action"
                                        label="Review Decision"
                                        value={selectedAction}
                                        onChange={handleActionChange}
                                        disabled={submitting}
                                    />
                                    {formErrors.action && (
                                        <div style={{ color: "red", marginTop: "5px", fontSize: "14px" }}>
                                            {formErrors.action}
                                        </div>
                                    )}
                                </div>

                                {/* Assignment Option and Dates - Only show when Approve is selected */}
                                {selectedAction === 'APPROVE' && (
                                    <>
                                        {/* Assignment Option Selection */}
                                        <div style={{ marginBottom: '20px', width: '100%' }}>
                                            <fieldset style={{ border: 'none', padding: 0 }}>
                                                <legend style={{
                                                    fontSize: '16px',
                                                    fontWeight: 'bold',
                                                    marginBottom: '10px',
                                                    color: '#1b1b1b'
                                                }}>
                                                    Assignment Option
                                                </legend>
                                                <div style={{ display: 'flex', gap: '20px', alignItems: 'center' }}>
                                                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                                        <input
                                                            type="radio"
                                                            name="assignOption"
                                                            value="immediate"
                                                            checked={assignOption === 'immediate'}
                                                            onChange={handleAssignOptionChange}
                                                            style={{
                                                                marginRight: '8px',
                                                                cursor: 'pointer',
                                                                accentColor: '#00607f'
                                                            }}
                                                        />
                                                        <span>Assign Immediately</span>
                                                    </label>
                                                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                                                        <input
                                                            type="radio"
                                                            name="assignOption"
                                                            value="date"
                                                            checked={assignOption === 'date'}
                                                            onChange={handleAssignOptionChange}
                                                            style={{
                                                                marginRight: '8px',
                                                                cursor: 'pointer',
                                                                accentColor: '#00607f'
                                                            }}
                                                        />
                                                        <span>Schedule Assignment</span>
                                                    </label>
                                                </div>
                                            </fieldset>
                                        </div>

                                        {/* Date Selection Section */}
                                        <div style={{ marginBottom: '20px', width: '100%' }}>
                                            <InputText
                                                label="Start Date"
                                                name="startDate"
                                                value={startDate}
                                                type="date"
                                                onChange={handleStartDateChange}
                                                mandatoryLabel={true}
                                                readOnly={assignOption === 'immediate' || submitting}
                                                min={(() => {
                                                    const today = new Date();
                                                    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
                                                })()}
                                            />
                                            {assignOption === 'immediate' && (
                                                <div style={{ color: "#666", marginTop: "5px", fontSize: "12px", fontStyle: "italic" }}>
                                                    Start date is set to today for immediate assignment
                                                </div>
                                            )}
                                            {formErrors.startDate && (
                                                <div style={{ color: "red", marginTop: "5px", fontSize: "14px" }}>
                                                    {formErrors.startDate}
                                                </div>
                                            )}
                                        </div>
                                        <div style={{ marginBottom: '20px', width: '100%' }}>
                                            <InputText
                                                label="End Date"
                                                name="endDate"
                                                value={endDate}
                                                type="date"
                                                onChange={handleEndDateChange}
                                                mandatoryLabel={true}
                                                readOnly={submitting}
                                            />
                                            {formErrors.endDate && (
                                                <div style={{ color: "red", marginTop: "5px", fontSize: "14px" }}>
                                                    {formErrors.endDate}
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}

                                {/* Review Notes Field */}
                                <div style={{ marginBottom: '30px' }}>
                                    <fieldset
                                        className={"field-wrapper"}
                                        style={{
                                            padding: "0",
                                            margin: "0",
                                            border: "none",
                                            outline: "none",
                                            width: "100%"
                                        }}
                                    >
                                        <label htmlFor="review-notes-editor" style={{
                                            display: "block",
                                            marginBottom: "0.5rem",
                                            fontSize: "16px",
                                            fontWeight: "normal",
                                            color: "#1b1b1b"
                                        }}>
                                            <b>Review Notes:</b>
                                            <span style={{ color: '#9a2125' }}>*</span>
                                        </label>
                                        <ReactQuill
                                            className="review-notes-editor"
                                            style={{
                                                border: '1px solid #3e3e3f',
                                                width: '100%',
                                                marginTop: '9px',
                                                overflowY: 'auto'
                                            }}
                                            value={reviewNotes}
                                            onChange={handleReviewNotesChange}
                                            modules={{
                                                toolbar: [
                                                    ["bold", "italic", "underline", "strike"],
                                                    [{list: "ordered"}, {list: "bullet"}],
                                                    [{align: []}],
                                                    ["clean"],
                                                ],
                                            }}
                                            readOnly={submitting}
                                        />
                                    </fieldset>
                                    {formErrors.reviewNotes && (
                                        <div style={{ color: "red", marginTop: "5px", fontSize: "14px" }}>
                                            {formErrors.reviewNotes}
                                        </div>
                                    )}
                                </div>
                            </div>


                            {/* Action Buttons */}
                            <div style={{
                                display: 'flex',
                                justifyContent: 'center',
                                gap: '20px',
                                marginTop: '30px'
                            }}>
                                <Button
                                    onClick={handleCancel}
                                    disabled={submitting}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    onClick={handleSubmit}
                                    disabled={!selectedAction || submitting || !reviewNotes.trim() || !!formErrors.startDate || !!formErrors.endDate}
                                >
                                    {submitting ? 'Submitting...' : 'Submit Decision'}
                                </Button>
                            </div>
                        </div>
                    ) : (
                        /* Back button only for non-REQUESTED status */
                        <div style={{
                            borderTop: '2px solid #dee2e6',
                            paddingTop: '30px',
                            marginTop: '30px',
                            display: 'flex',
                            justifyContent: 'center'
                        }}>
                            <Button onClick={handleCancel}>
                                Back to Request History
                            </Button>
                        </div>
                    )}
                </div>
            </div>
            <FooterData/>
        </React.Fragment>
    );
};

export default RequestDetails;